import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { DocumentTextIcon, TrashIcon, ArrowRightIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { Card, CardContent } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { useDrafts, Draft } from '../hooks/useDrafts';

export const DraftsPage: React.FC = () => {
  const navigate = useNavigate();
  const { drafts, deleteDraft, loading } = useDrafts();
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const handleResume = (draft: Draft) => {
    navigate('/projects/create', { state: { draft } });
  };

  const handleDeleteClick = (id: string) => {
    setConfirmDeleteId(id);
  };

  const handleConfirmDelete = async () => {
    if (!confirmDeleteId) return;
    setDeleting(true);
    try {
      await deleteDraft(confirmDeleteId);
    } finally {
      setDeleting(false);
      setConfirmDeleteId(null);
    }
  };

  const confirmDraft = drafts.find(d => d.id === confirmDeleteId);

  return (
    <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 space-y-8">
      <div>
        <h1 className="text-3xl font-black font-heading text-white uppercase tracking-tight">Project Drafts</h1>
        <p className="text-text-secondary text-sm font-medium mt-1">Resume or manage your unfinished project configurations.</p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center p-12">
          <div className="w-12 h-12 border-4 border-primary/20 border-t-primary rounded-full animate-spin" />
        </div>
      ) : drafts.length === 0 ? (
        <Card className="overflow-hidden border-white/10 shadow-2xl shadow-black/20 rounded-2xl bg-[#131B2C]/40 backdrop-blur-xl">
          <CardContent className="p-12 text-center flex flex-col items-center justify-center">
            <div className="p-4 bg-white/5 rounded-full mb-4">
              <DocumentTextIcon className="w-12 h-12 text-text-secondary/50" />
            </div>
            <h2 className="text-xl font-bold text-white uppercase tracking-tight mb-2">No Drafts Found</h2>
            <p className="text-text-secondary text-sm max-w-md mx-auto">
              You don't have any saved project drafts. When you leave the project creation page with unsaved changes, they can be saved here.
            </p>
            <Button onClick={() => navigate('/projects/create')} className="mt-6 bg-primary hover:bg-primary/90 text-white font-bold uppercase tracking-wider">
              Create New Project
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {drafts.map((draft) => (
            <Card key={draft.id} className="overflow-hidden border-white/10 shadow-xl shadow-black/20 rounded-2xl bg-[#131B2C]/60 backdrop-blur-xl group hover:border-primary/30 transition-all">
              <CardContent className="p-6 flex flex-col h-full">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-black text-white uppercase tracking-tight truncate" title={draft.name || 'Untitled Project'}>
                      {draft.name || 'Untitled Project'}
                    </h3>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="px-2 py-0.5 rounded-full bg-orange-500/10 text-orange-400 text-[10px] font-black uppercase tracking-widest border border-orange-500/20">
                        Draft
                      </span>
                      <span className="text-xs text-text-secondary/60 font-medium">
                        {format(new Date(draft.updatedAt || draft.createdAt || Date.now()), 'MMM d, yyyy • h:mm a')}
                      </span>
                    </div>
                  </div>
                </div>

                <p className="text-sm text-text-secondary line-clamp-3 mb-6 flex-1">
                  {draft.description || 'No description provided.'}
                </p>

                <div className="flex items-center gap-3 mt-auto pt-4 border-t border-white/5">
                  {/* Resume button — ghost style, text only changes on hover */}
                  <button
                    onClick={() => handleResume(draft)}
                    className="flex-1 h-10 flex items-center justify-center gap-2 rounded-xl text-sm font-bold uppercase tracking-wider text-white/70 bg-white/5 border border-white/8 hover:border-primary/40 hover:text-primary transition-all duration-200 group/btn"
                  >
                    Resume
                    <ArrowRightIcon className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform duration-200" />
                  </button>

                  {/* Delete button — red icon only */}
                  <button
                    onClick={() => handleDeleteClick(draft.id)}
                    title="Delete Draft"
                    className="h-10 w-10 flex items-center justify-center rounded-xl text-red-400 bg-red-500/5 border border-red-500/20 hover:bg-red-500/15 hover:border-red-500/40 transition-all duration-200"
                  >
                    <TrashIcon className="w-5 h-5" />
                  </button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {confirmDeleteId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => !deleting && setConfirmDeleteId(null)}
          />
          {/* Dialog */}
          <div className="relative z-10 w-full max-w-md bg-[#131B2C] border border-white/10 rounded-2xl shadow-2xl shadow-black/50 p-6 animate-in fade-in zoom-in-95 duration-200">
            <div className="flex items-start gap-4 mb-5">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-500/10 border border-red-500/20 flex items-center justify-center">
                <ExclamationTriangleIcon className="w-5 h-5 text-red-400" />
              </div>
              <div>
                <h3 className="text-white font-black uppercase tracking-tight text-base">Delete Draft?</h3>
                <p className="text-text-secondary text-sm mt-1">
                  Are you sure you want to permanently delete{' '}
                  <span className="text-white font-semibold">"{confirmDraft?.name || 'Untitled Project'}"</span>?
                  This action cannot be undone.
                </p>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={() => setConfirmDeleteId(null)}
                disabled={deleting}
                className="h-9 px-4 rounded-xl text-sm font-bold uppercase tracking-wider text-white/60 bg-white/5 border border-white/10 hover:bg-white/10 transition-all duration-200 disabled:opacity-40"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                disabled={deleting}
                className="h-9 px-4 rounded-xl text-sm font-bold uppercase tracking-wider text-white bg-red-500 hover:bg-red-600 border border-red-500/50 shadow-lg shadow-red-500/20 transition-all duration-200 disabled:opacity-60 flex items-center gap-2"
              >
                {deleting ? (
                  <>
                    <span className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <TrashIcon className="w-4 h-4" />
                    Delete
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
